import { useState, useEffect } from 'react';

function AddExtensions() {
  const [practiceId, setPracticeId] = useState('');
  const [mainNumber, setMainNumber] = useState('');
  const [backendServer, setBackendServer] = useState('great-civet-literally.ngrok-free.app');
  
  // Existing extensions state
  const [existingExtensions, setExistingExtensions] = useState([]);
  const [practiceInfo, setPracticeInfo] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  
  // New phones state
  const [newPhones, setNewPhones] = useState([]);
  const [voipModel, setVoipModel] = useState('T54W');
  
  // Teledynamics import
  const [showTeledynamicsInput, setShowTeledynamicsInput] = useState(false);
  const [teledynamicsData, setTeledynamicsData] = useState('');
  
  // Ring group option
  const [addToRingGroup, setAddToRingGroup] = useState(true);
  
  // Submit state
  const [submitStatus, setSubmitStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchExistingExtensions = async () => {
    if (!practiceId.trim()) {
      setFetchError('Please enter a practice ID');
      return;
    }

    setFetchLoading(true);
    setFetchError('');
    setExistingExtensions([]);
    setPracticeInfo(null);

    try {
      const response = await fetch(`https://${backendServer}/api/practice/${practiceId}/phones`, {
        headers: {
          'ngrok-skip-browser-warning': 'true'
        }
      });

      const data = await response.json();

      if (data.success) {
        setExistingExtensions(data.extensions || []);
        setPracticeInfo(data.practice_info || null);
        
        // Auto-fill main number if available from practice info
        if (data.practice_info?.main_number && !mainNumber) {
          setMainNumber(data.practice_info.main_number);
        }
        
        setFetchError('');
      } else {
        setFetchError(data.error || 'Failed to fetch extensions');
      }
    } catch (error) {
      setFetchError(`Error: ${error.message}`);
    } finally {
      setFetchLoading(false);
    }
  };

  const addNewPhone = () => {
    const nextExt = getNextExtensionNumber();
    setNewPhones(prev => [...prev, {
      name: `NewtonPhone${nextExt}`,
      ext: nextExt.toString(),
      outbound_ani: mainNumber,
      mac: '',
      serial: '',
      is_softphone: false,
      vendor: 'Yealink',
      model: voipModel,
      use_specific_model: false
    }]);
  };

  const getNextExtensionNumber = () => {
    // Filter out extension 88 (NewtonPhone88 is a special extension)
    const existingExts = existingExtensions
      .map(e => parseInt(e.name) || 0)
      .filter(ext => ext !== 88);
    const newExts = newPhones.map(p => parseInt(p.ext) || 0);
    const allExts = [...existingExts, ...newExts];
    return allExts.length > 0 ? Math.max(...allExts) + 1 : 1;
  };

  const removeNewPhone = (index) => {
    setNewPhones(prev => prev.filter((_, i) => i !== index));
  };

  const handlePhoneChange = (index, field, value) => {
    setNewPhones(prev => prev.map((phone, i) => {
      if (i === index) {
        const updated = { ...phone, [field]: value };
        if (field === 'use_specific_model' && !value) {
          updated.model = voipModel;
        }
        return updated;
      }
      return phone;
    }));
  };

  const validateMac = (mac) => {
    if (!mac.trim()) return 'empty';
    if (mac.length !== 12) return 'invalid_length';
    if (!/^[0-9A-Fa-f]{12}$/.test(mac)) return 'invalid_characters';
    return 'valid';
  };

  const validateSerial = (serial) => {
    if (!serial.trim()) return 'empty';
    if (serial.length !== 16) return 'invalid_length';
    if (!/^[0-9A-Za-z]+$/.test(serial)) return 'invalid_characters';
    return 'valid';
  };

  const getMacValidationStatus = (mac, currentIndex) => {
    if (mac.trim() !== '') {
      const duplicateMacIndex = newPhones.findIndex((phone, index) => 
        index !== currentIndex && phone.mac === mac
      );
      if (duplicateMacIndex !== -1) return 'duplicate';
      
      const existingMac = existingExtensions.find(ext => 
        ext.macs && ext.macs.includes(mac.toUpperCase())
      );
      if (existingMac) return 'duplicate';
    }
    return validateMac(mac);
  };

  const getSerialValidationStatus = (serial, currentIndex) => {
    if (serial.trim() !== '') {
      const duplicateSerialIndex = newPhones.findIndex((phone, index) => 
        index !== currentIndex && phone.serial === serial
      );
      if (duplicateSerialIndex !== -1) return 'duplicate';
    }
    return validateSerial(serial);
  };

  const getInputClassName = (status) => {
    switch (status) {
      case 'valid': return 'valid-input';
      case 'invalid_length': return 'invalid-length-input';
      case 'invalid_characters': return 'invalid-characters-input';
      case 'duplicate': return 'duplicate-input';
      default: return '';
    }
  };

  const handlePopulateTeledynamics = () => {
    if (!teledynamicsData.trim()) {
      setSubmitStatus('Please enter Teledynamics data');
      return;
    }

    try {
      const lines = teledynamicsData.trim().split('\n');
      const dataLines = lines.slice(1).filter(line => line.trim());
      
      if (dataLines.length === 0) {
        setSubmitStatus('No valid data found. Please check the format.');
        return;
      }

      const startExt = getNextExtensionNumber();
      const phones = dataLines.map((line, index) => {
        const parts = line.trim().split(/\s+/);
        if (parts.length < 2) {
          throw new Error(`Invalid format on line ${index + 2}: ${line}`);
        }
        
        const serial = parts[0];
        const mac = parts[1];
        
        if (!/^[0-9A-Fa-f]{12}$/.test(mac)) {
          throw new Error(`Invalid MAC address format on line ${index + 2}: ${mac}`);
        }
        if (!/^[0-9A-Za-z]{16}$/.test(serial)) {
          throw new Error(`Invalid serial number format on line ${index + 2}: ${serial}`);
        }
        
        return {
          name: `NewtonPhone${startExt + index}`,
          ext: (startExt + index).toString(),
          outbound_ani: mainNumber,
          mac: mac.toUpperCase(),
          serial: serial.toUpperCase(),
          is_softphone: false,
          vendor: 'Yealink',
          model: voipModel,
          use_specific_model: false
        };
      });

      setNewPhones(prev => [...prev, ...phones]);
      setSubmitStatus(`✅ Added ${phones.length} phones from Teledynamics data`);
      setTeledynamicsData('');
      
    } catch (error) {
      setSubmitStatus(`❌ Error parsing Teledynamics data: ${error.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!practiceId.trim()) {
      setSubmitStatus('Please enter a practice ID and fetch existing extensions first');
      return;
    }

    if (newPhones.length === 0) {
      setSubmitStatus('Please add at least one new phone');
      return;
    }

    // Validate phones
    for (let i = 0; i < newPhones.length; i++) {
      const phone = newPhones[i];
      if (!phone.name.trim()) {
        setSubmitStatus(`Please enter name for phone ${i + 1}`);
        return;
      }
      if (!phone.is_softphone) {
        const macStatus = getMacValidationStatus(phone.mac, i);
        if (macStatus !== 'valid') {
          setSubmitStatus(`Invalid MAC address for ${phone.name}: ${macStatus}`);
          return;
        }
        const serialStatus = getSerialValidationStatus(phone.serial, i);
        if (serialStatus !== 'valid') {
          setSubmitStatus(`Invalid serial number for ${phone.name}: ${serialStatus}`);
          return;
        }
      }
    }

    setIsSubmitting(true);
    setSubmitStatus('Adding phones...');

    try {
      const payload = {
        practice_id: practiceId,
        main_number: mainNumber,
        add_to_ring_group: addToRingGroup,
        phones: newPhones.map(phone => {
          const data = {
            name: phone.name,
            ext: phone.ext,
            outbound_ani: phone.outbound_ani || mainNumber,
          };
          if (!phone.is_softphone) {
            data.mac = phone.mac;
            data.serial = phone.serial;
            data.vendor = phone.vendor;
            data.model = phone.model;
          }
          return data;
        })
      };

      const response = await fetch(`https://${backendServer}/api/practice/${practiceId}/phones`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        const stats = result.data?.statistics || {};
        let successMessage = `✅ ${result.message}\n\n`;
        successMessage += `📊 Summary:\n`;
        successMessage += `• ${stats.extensions_added || 0} extensions added\n`;
        successMessage += `• ${stats.devices_added || 0} devices provisioned\n`;
        successMessage += `• Ring group: ${stats.ring_group_updated ? 'Updated' : 'Not updated'}`;

        setSubmitStatus(successMessage);
        setNewPhones([]);
        
        // Refresh existing extensions
        await fetchExistingExtensions();
      } else {
        setSubmitStatus(`❌ Error: ${result.error || 'Failed to add phones'}`);
      }
    } catch (error) {
      setSubmitStatus(`❌ Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update outbound_ani when mainNumber changes
  useEffect(() => {
    if (mainNumber) {
      setNewPhones(prev => prev.map(phone => ({
        ...phone,
        outbound_ani: phone.outbound_ani || mainNumber
      })));
    }
  }, [mainNumber]);

  // Update model for phones without specific model when voipModel changes
  useEffect(() => {
    setNewPhones(prev => prev.map(phone => ({
      ...phone,
      model: phone.use_specific_model ? phone.model : voipModel
    })));
  }, [voipModel]);

  return (
    <div className="tab-content">
      <h1>Add Extensions to Existing Practice</h1>
      
      <form onSubmit={handleSubmit} className="voip-config-form">
        {/* Backend Server Configuration */}
        <div className="config-section">
          <h2>Backend Server Configuration</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="backend_server">Backend Provisioning Server:</label>
              <input
                type="text"
                id="backend_server"
                value={backendServer}
                onChange={(e) => setBackendServer(e.target.value)}
                placeholder="great-civet-literally.ngrok-free.app"
              />
            </div>
          </div>
          <div className="server-recommendations">
            <h3>Recommended Servers:</h3>
            <ul>
              <li 
                className="clickable-server"
                onClick={() => setBackendServer('great-civet-literally.ngrok-free.app')}
              >
                great-civet-literally.ngrok-free.app
              </li>
              <li 
                className="clickable-server"
                onClick={() => setBackendServer('newtonflaskserver-hfgkcrewf4fgedc6.westus-01.azurewebsites.net')}
              >
                newtonflaskserver-hfgkcrewf4fgedc6.westus-01.azurewebsites.net
              </li>
            </ul>
          </div>
        </div>

        {/* Practice Lookup */}
        <div className="config-section">
          <h2>Practice Lookup</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="practice_id">Practice ID:</label>
              <input
                type="text"
                id="practice_id"
                value={practiceId}
                onChange={(e) => setPracticeId(e.target.value)}
                placeholder="practice-1234567890"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), fetchExistingExtensions())}
              />
            </div>
            <div className="form-group">
              <label htmlFor="main_number">Main Number:</label>
              <input
                type="tel"
                id="main_number"
                value={mainNumber}
                onChange={(e) => setMainNumber(e.target.value)}
                placeholder="+16457771667"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={fetchExistingExtensions}
            disabled={fetchLoading}
            className="add-user-btn"
            style={{ marginTop: '10px' }}
          >
            {fetchLoading ? 'Fetching...' : 'Fetch Existing Extensions'}
          </button>
          
          {fetchError && (
            <div className="status-message error" style={{ marginTop: '10px' }}>
              {fetchError}
            </div>
          )}
        </div>

        {/* Practice Info */}
        {practiceInfo && (
          <div className="config-section">
            <h2>Practice Information</h2>
            <div className="resolved-domain-info">
              <div className="domain-info-item">
                <span className="domain-label">Domain:</span>
                <span className="domain-value">{practiceInfo.vodia_domain || 'Not configured'}</span>
              </div>
              <div className="domain-info-item">
                <span className="domain-label">FQDN:</span>
                <span className="domain-value">{practiceInfo.fqdn || 'Not configured'}</span>
              </div>
              <div className="domain-info-item">
                <span className="domain-label">Total Extensions:</span>
                <span className="domain-value">{existingExtensions.length}</span>
              </div>
            </div>
          </div>
        )}

        {/* Existing Extensions Table */}
        {existingExtensions.length > 0 && (
          <div className="config-section">
            <h2>Existing Extensions ({existingExtensions.length})</h2>
            <div className="extensions-table-container">
              <table className="extensions-table">
                <thead>
                  <tr>
                    <th>Extension</th>
                    <th>Display Name</th>
                    <th>ANI</th>
                    <th>MAC Address</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {existingExtensions.map((ext) => (
                    <tr key={ext.name}>
                      <td><strong>{ext.name}</strong></td>
                      <td>{ext.display_name || '-'}</td>
                      <td>{ext.ani || '-'}</td>
                      <td>{ext.macs && ext.macs.length > 0 ? ext.macs.join(', ') : '-'}</td>
                      <td>{ext.macs && ext.macs.length > 0 ? 'Physical' : 'Softphone'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* New Phones Configuration */}
        {practiceInfo && (
          <>
            <div className="config-section">
              <h2>Add New Phones</h2>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="voip_model">Default VoIP Model:</label>
                  <select
                    id="voip_model"
                    value={voipModel}
                    onChange={(e) => setVoipModel(e.target.value)}
                  >
                    <option value="T54W">T54W</option>
                    <option value="T57W">T57W</option>
                    <option value="T46S">T46S</option>
                    <option value="T46U">T46U</option>
                    <option value="W60B">W60B</option>
                    <option value="W70B">W70B</option>
                    <option value="T33G">T33G</option>
                    <option value="T42S">T42S</option>
                  </select>
                </div>
              </div>

              {/* Add to Ring Group */}
              <div className="form-group">
                <div className="checkbox-container">
                  <input
                    type="checkbox"
                    id="add_to_ring_group"
                    checked={addToRingGroup}
                    onChange={(e) => setAddToRingGroup(e.target.checked)}
                  />
                  <label htmlFor="add_to_ring_group">Add to main ring group</label>
                </div>
                <small className="help-text">
                  When checked, new extensions will be added to the main ring group (pmain)
                </small>
              </div>

              {/* Teledynamics Import */}
              <div className="form-group">
                <div className="checkbox-container">
                  <input
                    type="checkbox"
                    id="populate_teledynamics"
                    checked={showTeledynamicsInput}
                    onChange={(e) => setShowTeledynamicsInput(e.target.checked)}
                  />
                  <label htmlFor="populate_teledynamics">Import from Teledynamics</label>
                </div>
              </div>
              
              {showTeledynamicsInput && (
                <div className="form-group">
                  <label htmlFor="teledynamics_data">Teledynamics Data:</label>
                  <textarea
                    id="teledynamics_data"
                    value={teledynamicsData}
                    onChange={(e) => setTeledynamicsData(e.target.value)}
                    placeholder="Serial Number	Mac Address&#10;201081E061200386 805E0CDC73D3&#10;201081E061200387 805E0CDC73D4"
                    rows={6}
                    style={{ fontFamily: 'monospace', fontSize: '14px' }}
                  />
                  <small className="help-text">
                    Format: Serial Number [tab/space] Mac Address (one per line). Header line ignored.
                  </small>
                  <div style={{ marginTop: '10px' }}>
                    <button
                      type="button"
                      onClick={handlePopulateTeledynamics}
                      className="add-user-btn"
                      style={{ marginRight: '10px' }}
                    >
                      Import Phones
                    </button>
                    <button
                      type="button"
                      onClick={() => { setTeledynamicsData(''); setShowTeledynamicsInput(false); }}
                      className="remove-user-btn"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* New Phones List */}
            <div className="config-section">
              <div className="section-header">
                <h2>New Phones ({newPhones.length})</h2>
                <button type="button" onClick={addNewPhone} className="add-user-btn">
                  Add Phone
                </button>
              </div>

              {newPhones.length === 0 && (
                <div className="no-additional-numbers">
                  <p>No new phones added. Click "Add Phone" or import from Teledynamics.</p>
                </div>
              )}

              {newPhones.map((phone, index) => (
                <div key={index} className="user-config">
                  <div className="user-header">
                    <h3>New Phone {index + 1}</h3>
                    <button
                      type="button"
                      onClick={() => removeNewPhone(index)}
                      className="remove-user-btn"
                    >
                      Remove
                    </button>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Name:</label>
                      <input
                        type="text"
                        value={phone.name}
                        onChange={(e) => handlePhoneChange(index, 'name', e.target.value)}
                        placeholder={`NewtonPhone${phone.ext}`}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Extension:</label>
                      <input
                        type="text"
                        value={phone.ext}
                        onChange={(e) => handlePhoneChange(index, 'ext', e.target.value)}
                        placeholder="Extension number"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <div className="checkbox-container">
                      <input
                        type="checkbox"
                        id={`specific_model_${index}`}
                        checked={phone.use_specific_model}
                        onChange={(e) => handlePhoneChange(index, 'use_specific_model', e.target.checked)}
                      />
                      <label htmlFor={`specific_model_${index}`}>Select specific model</label>
                    </div>
                  </div>

                  {phone.use_specific_model && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>Model:</label>
                        <select
                          value={phone.model}
                          onChange={(e) => handlePhoneChange(index, 'model', e.target.value)}
                        >
                          <option value="T54W">T54W</option>
                          <option value="T57W">T57W</option>
                          <option value="T46S">T46S</option>
                          <option value="T46U">T46U</option>
                          <option value="W60B">W60B</option>
                          <option value="W70B">W70B</option>
                          <option value="T33G">T33G</option>
                          <option value="T42S">T42S</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {!phone.use_specific_model && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>Model:</label>
                        <input
                          type="text"
                          value={`${phone.model} (from default)`}
                          readOnly
                          className="readonly"
                        />
                      </div>
                    </div>
                  )}

                  <div className="form-group">
                    <div className="checkbox-container">
                      <input
                        type="checkbox"
                        id={`softphone_${index}`}
                        checked={phone.is_softphone}
                        onChange={(e) => handlePhoneChange(index, 'is_softphone', e.target.checked)}
                      />
                      <label htmlFor={`softphone_${index}`}>Softphone (no physical device)</label>
                    </div>
                  </div>

                  {!phone.is_softphone && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>MAC Address:</label>
                        <input
                          type="text"
                          value={phone.mac}
                          onChange={(e) => handlePhoneChange(index, 'mac', e.target.value)}
                          placeholder="805E0CDC73D3"
                          className={getInputClassName(getMacValidationStatus(phone.mac, index))}
                        />
                        <small className="help-text">
                          12 hexadecimal characters (0-9, A-F)
                          {phone.mac.length > 0 && (
                            <span className={getMacValidationStatus(phone.mac, index) === 'valid' ? 'valid' : 'invalid'}>
                              {' '}{phone.mac.length}/12
                              {getMacValidationStatus(phone.mac, index) === 'duplicate' && ' (Duplicate)'}
                            </span>
                          )}
                        </small>
                      </div>
                      <div className="form-group">
                        <label>Serial Number:</label>
                        <input
                          type="text"
                          value={phone.serial}
                          onChange={(e) => handlePhoneChange(index, 'serial', e.target.value)}
                          placeholder="201081E061200386"
                          className={getInputClassName(getSerialValidationStatus(phone.serial, index))}
                        />
                        <small className="help-text">
                          16 alphanumeric characters
                          {phone.serial.length > 0 && (
                            <span className={getSerialValidationStatus(phone.serial, index) === 'valid' ? 'valid' : 'invalid'}>
                              {' '}{phone.serial.length}/16
                              {getSerialValidationStatus(phone.serial, index) === 'duplicate' && ' (Duplicate)'}
                            </span>
                          )}
                        </small>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <button
              type="submit"
              className="submit-button"
              disabled={isSubmitting || newPhones.length === 0}
            >
              {isSubmitting ? 'Adding Phones...' : `Add ${newPhones.length} Phone${newPhones.length !== 1 ? 's' : ''}`}
            </button>
          </>
        )}

        {submitStatus && (
          <div className={`status-message ${submitStatus.includes('Error') || submitStatus.includes('❌') ? 'error' : 'success'}`}>
            {submitStatus}
          </div>
        )}
      </form>
    </div>
  );
}

export default AddExtensions;
